export const SHAPE_LONGITUDE_MIN = -180;
export const SHAPE_LONGITUDE_MAX = 180;
export const SHAPE_LATITUDE_MIN = -90;
export const SHAPE_LATITUDE_MAX = 90;
const POLAR_LATITUDE_EPSILON = 1e-9;

const isPolarLatitude = (latitude: number) =>
  Math.abs(Math.abs(latitude) - SHAPE_LATITUDE_MAX) <= POLAR_LATITUDE_EPSILON;

export const isPointWithinShapeCoordinateBounds = (point: any) => {
  if (
    !Array.isArray(point) ||
    point.length < 2 ||
    !Number.isFinite(point[0]) ||
    !Number.isFinite(point[1])
  ) {
    return false;
  }

  const longitude = point[0];
  const latitude = point[1];

  if (latitude < SHAPE_LATITUDE_MIN || latitude > SHAPE_LATITUDE_MAX) {
    return false;
  }

  // 极点位置经度无实际意义，不做经度范围拦截。
  if (isPolarLatitude(latitude)) {
    return true;
  }

  return longitude >= SHAPE_LONGITUDE_MIN && longitude <= SHAPE_LONGITUDE_MAX;
};

export const areCoordinatesWithinShapeBounds = (coordinates: any): boolean => {
  if (!Array.isArray(coordinates)) {
    return false;
  }

  if (typeof coordinates[0] === "number") {
    return isPointWithinShapeCoordinateBounds(coordinates);
  }

  return coordinates.every((item) => areCoordinatesWithinShapeBounds(item));
};

export const getValidPolygonRingPoints = (
  ring: any
): Array<[number, number]> => {
  if (!Array.isArray(ring)) {
    return [];
  }

  return ring
    .filter(
      (point: any) =>
        Array.isArray(point) &&
        point.length >= 2 &&
        Number.isFinite(point[0]) &&
        Number.isFinite(point[1])
    )
    .map((point: any) => [point[0], point[1]] as [number, number]);
};

const getUniquePolygonPoints = (points: Array<[number, number]>) => {
  return Array.from(new Set(points.map(([lng, lat]) => `${lng},${lat}`))).map(
    (key) => {
      const [lng, lat] = key.split(",").map(Number);
      return [lng, lat] as [number, number];
    }
  );
};

const closePolygonRing = (points: Array<[number, number]>) => {
  if (points.length === 0) {
    return points;
  }

  const [firstLng, firstLat] = points[0];
  const [lastLng, lastLat] = points[points.length - 1];

  if (firstLng === lastLng && firstLat === lastLat) {
    return points;
  }

  return [...points, points[0]];
};

export const hasPositivePolygonRingArea = (ring: any): boolean => {
  const points = getValidPolygonRingPoints(ring);
  const uniquePoints = getUniquePolygonPoints(points);

  if (uniquePoints.length < 3) {
    return false;
  }

  const closedPoints = closePolygonRing(points);
  if (closedPoints.length < 4) {
    return false;
  }

  let doubleArea = 0;
  for (let i = 0; i < closedPoints.length - 1; i++) {
    const [x1, y1] = closedPoints[i];
    const [x2, y2] = closedPoints[i + 1];
    doubleArea += x1 * y2 - x2 * y1;
  }

  return Math.abs(doubleArea / 2) > 0;
};

export const isPolygonCoordinatesSavable = (coordinates: any): boolean => {
  if (!Array.isArray(coordinates) || coordinates.length !== 1) {
    return false;
  }

  return hasPositivePolygonRingArea(coordinates[0]);
};
